import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import NewsCard from '@/components/feature/NewsCard';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const news = newsList.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const relatedDrama = news ? dramas.find((item) => item.id === news.relatedDramaId) : undefined;
  const moreNews = newsList.filter((item) => item.id !== id).slice(0, 3);

  usePageMeta(
    news ? `${news.title} - 详细剧情与资讯` : '花絮未找到 - 我妻子的一切',
    news ? news.excerpt : '未找到对应的花絮资讯。',
    news ? news.tags.join(',') : '',
  );

  useJsonLd(
    'ld-news',
    news
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: news.title,
          description: news.excerpt,
          image: news.cover,
          datePublished: news.date,
          articleSection: '花絮资讯',
          keywords: news.tags.join(','),
          inLanguage: 'zh-CN',
          url: `https://gdrongshida.cn/detail/news/${news.id}`,
        })
      : null,
  );

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!news) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到这篇花絮"
          description="内容可能已被移除或链接有误，返回花絮分类继续浏览吧。"
          actionLabel="返回花絮分类"
          actionTo="/category/花絮"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[{ label: '首页', to: '/' }, { label: '花絮', to: '/category/花絮' }, { label: news.title }]}
      />

      <article className="mx-auto mt-6 max-w-3xl">
        <header>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
              花絮资讯
            </span>
            <span className="flex items-center gap-1 text-sm text-foreground-400">
              <i className="ri-time-line" />
              {news.date}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
            {news.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {news.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                {tag}
              </span>
            ))}
          </div>
        </header>

        <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-80">
          <img
            src={news.cover}
            alt={news.title}
            title={`${news.title} - 花絮资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {news.body.map((paragraph, index) => (
            <p key={index} className="text-[15px] leading-loose text-foreground-700">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
          <div>
            <p className="font-heading text-sm font-bold text-foreground-900">分享这篇花絮</p>
            <p className="mt-1 text-xs text-foreground-400">分享功能为前端演示，复制链接即可分享给好友。</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleShare}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
              aria-label="复制链接"
            >
              <i className="ri-links-line" />
            </button>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500 text-background-50 transition-colors hover:bg-secondary-600">
              <i className="ri-weibo-line" />
            </span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500 text-background-50 transition-colors hover:bg-secondary-600">
              <i className="ri-wechat-line" />
            </span>
            {copied && <span className="text-sm font-label text-primary-600">链接已复制</span>}
          </div>
        </div>

        {relatedDrama && (
          <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-links-line text-primary-500" />
              相关电视剧
            </h2>
            <Link
              to={`/detail/tv/${relatedDrama.id}`}
              className="group mt-4 flex items-center gap-4"
            >
              <span className="h-24 w-16 shrink-0 overflow-hidden rounded-md">
                <img
                  src={relatedDrama.cover}
                  alt={relatedDrama.title}
                  title={`${relatedDrama.title} - 电视剧详情`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-500">
                  {relatedDrama.title}
                </span>
                <span className="mt-1 block text-xs text-foreground-400">
                  {relatedDrama.genres.join(' · ')} · 全 {relatedDrama.episodes} 集
                </span>
                <span className="mt-2 line-clamp-2 text-sm text-foreground-500">{relatedDrama.synopsis}</span>
              </span>
            </Link>
          </section>
        )}
      </article>

      <section className="mx-auto mt-12 max-w-5xl">
        <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1 rounded-full bg-accent-500" />
          更多花絮推荐
        </h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {moreNews.map((item) => (
            <NewsCard key={item.id} news={item} />
          ))}
        </div>
      </section>
    </div>
  );
}